import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  type DocumentData,
  type QueryConstraint,
  type Unsubscribe
} from "firebase/firestore";
import { firebase } from "@core/firebase/app";

export type QueryFilter = {
  field: string;
  operator:
    | "=="
    | "!="
    | "<"
    | "<="
    | ">"
    | ">="
    | "array-contains"
    | "array-contains-any"
    | "in"
    | "not-in";
  value: unknown;
};

export type OrderByClause = {
  field: string;
  direction: "asc" | "desc";
};

class FirestoreService {
  private static instance: FirestoreService;

  static getInstance(): FirestoreService {
    if (!FirestoreService.instance) {
      FirestoreService.instance = new FirestoreService();
    }
    return FirestoreService.instance;
  }

  async getDocument(collectionPath: string, documentId: string): Promise<(DocumentData & { id: string }) | null> {
    const ref = doc(firebase.firestore, collectionPath, documentId);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...(snapshot.data() as DocumentData) };
  }

  async setDocument(collectionPath: string, documentId: string, data: DocumentData, merge = true): Promise<void> {
    const ref = doc(firebase.firestore, collectionPath, documentId);
    await setDoc(ref, data, { merge });
  }

  async updateDocument(collectionPath: string, documentId: string, data: DocumentData): Promise<void> {
    const ref = doc(firebase.firestore, collectionPath, documentId);
    await updateDoc(ref, data);
  }

  async addDocument(collectionPath: string, data: DocumentData): Promise<string> {
    const ref = collection(firebase.firestore, collectionPath);
    const docRef = await addDoc(ref, data);
    return docRef.id;
  }

  async queryCollection(
    collectionPath: string,
    filters: QueryFilter[] = [],
    order?: OrderByClause,
    limitCount?: number
  ): Promise<Array<DocumentData & { id: string }>> {
    const constraints: QueryConstraint[] = [];
    filters.forEach((filter) => {
      constraints.push(where(filter.field, filter.operator as any, filter.value));
    });
    if (order) {
      constraints.push(orderBy(order.field, order.direction));
    }
    if (limitCount) {
      constraints.push(limit(limitCount));
    }
    const ref = query(collection(firebase.firestore, collectionPath), ...constraints);
    const snapshot = await getDocs(ref);
    return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as DocumentData) }));
  }

  subscribeToDocument(
    collectionPath: string,
    documentId: string,
    callback: (doc: (DocumentData & { id: string }) | null) => void
  ): Unsubscribe {
    const ref = doc(firebase.firestore, collectionPath, documentId);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        callback({ id: snap.id, ...(snap.data() as DocumentData) });
      },
      () => callback(null)
    );
  }

  subscribeToCollection(
    collectionPath: string,
    filters: QueryFilter[] = [],
    order?: OrderByClause,
    limitCount?: number,
    callback?: (docs: Array<DocumentData & { id: string }>) => void
  ): Unsubscribe {
    const constraints: QueryConstraint[] = [];
    filters.forEach((filter) => constraints.push(where(filter.field, filter.operator as any, filter.value)));
    if (order) {
      constraints.push(orderBy(order.field, order.direction));
    }
    if (limitCount) {
      constraints.push(limit(limitCount));
    }
    const ref = query(collection(firebase.firestore, collectionPath), ...constraints);
    return onSnapshot(
      ref,
      (snapshot) => {
        const docs = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as DocumentData) }));
        callback?.(docs);
      },
      () => {
        callback?.([] as Array<DocumentData & { id: string }>);
      }
    );
  }


  serverTimestamp() {
    return serverTimestamp();
  }
}

export const firestoreService = FirestoreService.getInstance();



